import * as Cesium from 'cesium'

export default {
  directions: {
    LEFT: 0,
    RIGHT: 1,
    BOTTOM: 2,
    FRONT: 3
  },

  removePrimitivesFromPC(primitiveCollection) {
    if (primitiveCollection) {
      primitiveCollection.removeAll()
    }
  },

  removeEntitiesFromDS(dataSource) {
    if (dataSource && dataSource.entities) {
      for (let i = 0; i < dataSource.entities.values.length; i++) {
        dataSource.entities.removeById(i)
      }
    }
  },

  clampLongitude(radians) {
    return Cesium.Math.convertLongitudeRange(radians)
  },

  clampLatitude(radians) {
    return Cesium.Math.clampToLatitudeRange(radians)
  },

  clampToPI(radians) {
    const pi = Cesium.Math.PI
    if (radians > pi || radians < -pi) {
      return this.avoidExtremeLongitudes(-radians % pi)
    } else {
      return this.avoidExtremeLongitudes(radians)
    }
  },

  toRadians(degrees) {
    return Cesium.Math.toRadians(degrees)
  },

  toDegrees(radians) {
    return Cesium.Math.toDegrees(radians)
  },

  avoidExtremeLongitudes(lon, isDegree = true) {
    const extremeValue = isDegree ? 180 : Math.PI
    if (lon === extremeValue) {
      lon -= 1e-8
    } else if (lon === -extremeValue) {
      lon += 1e-8
    }
    return lon
  },

  avoidExtremeLatitudes(lat, isDegree = true) {
    const extremeValue = isDegree ? 90 : Math.PI / 2
    if (lat === extremeValue) {
      lat -= 1e-6
    } else if (lat === -extremeValue) {
      lat += 1e-6
    }
    return lat
  },

  getMinLonDegrees(a, b) {
    return Math.min(
      this.clampLongitude(this.toRadians(a)),
      this.clampLongitude(this.toRadians(b))
    )
  },

  getMaxLonDegrees(a, b) {
    return Math.max(
      this.clampLongitude(this.toRadians(a)),
      this.clampLongitude(this.toRadians(b))
    )
  },

  getMinLatDegrees(a, b) {
    return Math.min(
      this.clampLatitude(this.toRadians(a)),
      this.clampLatitude(this.toRadians(b))
    )
  },

  getMaxLatDegrees(a, b) {
    return Math.max(
      this.clampLatitude(this.toRadians(a)),
      this.clampLatitude(this.toRadians(b))
    )
  },

  getMinLonRadians(a, b) {
    return Math.min(this.clampLongitude(a), this.clampLongitude(b))
  },

  getMaxLonRadians(a, b) {
    return Math.max(this.clampLongitude(a), this.clampLongitude(b))
  },

  getMinLatRadians(a, b) {
    return Math.min(this.clampLatitude(a), this.clampLatitude(b))
  },

  getMaxLatRadians(a, b) {
    return Math.max(this.clampLatitude(a), this.clampLatitude(b))
  },

  createColorsArray(nbOfColors) {
    let colorsArray = []
    const randomHSLValue = (h) => [
      h,
      (Math.floor(Math.random() * (80 - 60 + 1)) + 80) / 100,
      (Math.floor(Math.random() * (50 - 40 + 1)) + 40) / 100
    ]
    let hValues = []
    for (let i = 0; i < nbOfColors; i++) {
      hValues.push(i / nbOfColors)
    }
    hValues.forEach((hue) => {
      const rgba = this.hslToRgb(randomHSLValue(hue)).map(
        (value) => value / 255
      )
      colorsArray.push(new Cesium.Color(rgba[0], rgba[1], rgba[2], rgba[3]))
    })
    return colorsArray
  },

  /**
   * Converts an HSL color value to RGB. Conversion formula
   * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
   * Assumes h, s, and l are contained in the set [0, 1] and
   * returns r, g, and b in the set [0, 255].
   *
   * @param   {number}  h       The hue
   * @param   {number}  s       The saturation
   * @param   {number}  l       The lightness
   * @return  {Array}           The RGB representation
   */
  hslToRgb(hslArray) {
    let r, g, b
    const [h, s, l] = hslArray
    if (s == 0) {
      r = g = b = l // achromatic
    } else {
      function hue2rgb(p, q, t) {
        if (t < 0) t += 1
        if (t > 1) t -= 1
        if (t < 1 / 6) {
          return p + (q - p) * 6 * t
        }
        if (t < 1 / 2) {
          return q
        }
        if (t < 2 / 3) {
          return p + (q - p) * (2 / 3 - t) * 6
        }
        return p
      }

      let q = l < 0.5 ? l * (1 + s) : l + s - l * s
      let p = 2 * l - q

      r = hue2rgb(p, q, h + 1 / 3)
      g = hue2rgb(p, q, h)
      b = hue2rgb(p, q, h - 1 / 3)
    }
    return [r * 255, g * 255, b * 255, 255]
  }
}
